import { useState } from "react";
import { SHOP_TYPE_LABELS, type BoutiqueCardDTO } from "@easyshop/shared";
import { cx } from "@/components/ui";
import { DeltaPill } from "@/components/Rankings";
import { eur } from "@/lib/format";

type Filter = "all" | "INTEGRATED" | "FRANCHISE";
const FILTERS: { key: Filter; label: string }[] = [
  { key: "all", label: "Toutes" },
  { key: "INTEGRATED", label: "Intégrées" },
  { key: "FRANCHISE", label: "Franchises" },
];

/**
 * Cartes boutiques du bas de l'accueil : 2 colonnes, liseré vert (intégrée) ou
 * violet (franchise), triées par CA. Filtre par type si le périmètre mêle les deux.
 */
export function BoutiqueCards({
  title,
  boutiques,
  cmpLabel,
  onSelect,
}: {
  title: string;
  boutiques: BoutiqueCardDTO[];
  /** Période comparée en bref (« mer. dernier »). */
  cmpLabel: string;
  onSelect: (id: string) => void;
}) {
  const [filter, setFilter] = useState<Filter>("all");
  const mixed = boutiques.some((b) => b.type === "INTEGRATED") && boutiques.some((b) => b.type === "FRANCHISE");
  const shown = boutiques.filter((b) => filter === "all" || b.type === filter).sort((x, y) => y.ca - x.ca);

  return (
    <section className="pt-[14px]">
      <div className="flex items-baseline justify-between gap-2 px-1">
        <h2 className="text-[20px] font-bold tracking-[-0.3px]">{title}</h2>
        <span className="text-[14px] text-muted-2">
          {shown.length} sur {boutiques.length}
        </span>
      </div>
      {mixed && (
        <div className="no-scrollbar mt-[10px] flex gap-2 overflow-x-auto">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              aria-pressed={filter === f.key}
              onClick={() => setFilter(f.key)}
              className={cx(
                "min-h-[32px] flex-shrink-0 rounded-full px-[14px] text-[14px] font-semibold",
                filter === f.key ? "bg-seg-active text-white" : "bg-white text-ink-2",
              )}
            >
              {f.label}
            </button>
          ))}
        </div>
      )}
      {boutiques.length === 0 ? (
        <div className="mt-3 rounded-[22px] bg-white px-4 py-5 text-center text-[15px] text-muted">Aucune boutique.</div>
      ) : (
        <div className="mt-3 grid grid-cols-2 gap-[10px]">
          {shown.map((b) => {
            const color = b.type === "FRANCHISE" ? "var(--franchise-vivid)" : "var(--integrated)";
            const obj = b.objectivePct == null ? null : Math.round(b.objectivePct);
            return (
              <button
                key={b.id}
                type="button"
                onClick={() => onSelect(b.id)}
                className={cx("min-w-0 overflow-hidden rounded-[22px] border-t-4 bg-white p-[14px] text-left", !b.synced && "opacity-70")}
                style={{ borderTopColor: color }}
              >
                <span className="flex items-center justify-between gap-[6px]">
                  <span className="min-w-0 truncate text-[15px] font-semibold">{b.city}</span>
                  <DeltaPill delta={b.delta} arrow className="px-2 py-[3px] text-[12px]" />
                </span>
                <span className="mt-[2px] flex items-center gap-[5px] text-[12px] text-muted-2">
                  <span className="h-[7px] w-[7px] rounded-full" style={{ background: color }} />
                  {b.synced ? SHOP_TYPE_LABELS[b.type] : "Non synchronisée"}
                </span>
                <span className="mt-2 block truncate text-[21px] font-bold tracking-[-0.4px] tabular">{eur(b.ca)}</span>
                <span className="block min-h-[18px] truncate text-[12px] tabular text-muted-2">
                  {b.prevCa != null && `${cmpLabel} ${eur(b.prevCa)}`}
                </span>
                {obj != null && (
                  <span className="mt-2 flex items-center gap-2">
                    <span className="h-[5px] flex-1 overflow-hidden rounded-[3px] bg-soft-fill">
                      <span className="block h-full rounded-[3px]" style={{ width: `${Math.min(100, obj)}%`, background: color }} />
                    </span>
                    <span className="text-[11px] tabular text-muted-2">{obj} %</span>
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </section>
  );
}
